import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { assetUrl } from "../utils/api";
import { coReadGet, coReadPost } from "../api/coReadApi";

const SUBS = [
  ["home", "共读首页"],
  ["assist", "AI 阅读助手"],
  ["plan", "我的计划"],
  ["discuss", "共读讨论"],
  ["summary", "成果汇总"],
  ["chapters", "书籍与章节"],
  ["mine", "我的成长"],
];

export default function CoReadCenterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = SUBS.some(([k]) => k === searchParams.get("sub"))
    ? searchParams.get("sub")
    : "home";
  const userId = Number(localStorage.getItem("user_id") || 0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useSeo({
    title: "AI 共读中心 - 读书会",
    metaDescription: "上传电子档后，用 AI 辅助理解章节、生成讨论问题与个人阅读记录。",
    path: "/book-club/ai-center",
    noindex: true,
  });

  const load = () => {
    setLoading(true);
    setError("");
    const q = userId ? `?user_id=${userId}` : "";
    coReadGet(`/home${q}`)
      .then(setData)
      .catch((e) => setError(e.message || "加载失败"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const setSub = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "home") next.delete("sub");
    else next.set("sub", key);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-pink-50 to-purple-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-sm text-gray-500">
            <Link to="/book-club" className="text-pink-600 hover:underline">
              读书会
            </Link>
            <span className="mx-2">/</span>
            <span>AI 共读中心</span>
          </div>

          <header className="text-center space-y-2">
            <p className="text-sm text-amber-700 font-medium">读书会 · 增量功能</p>
            <h1 className="text-3xl font-bold text-gray-800">AI 共读中心</h1>
            <p className="text-gray-500 text-sm">
              值日表同步计划 · 自主打卡 · AI 理解 · 共读交流
            </p>
          </header>

          <div className="flex justify-center flex-wrap gap-2">
            {SUBS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSub(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border ${
                  sub === key
                    ? "bg-pink-500 text-white border-pink-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center text-gray-400 py-16">加载中...</div>
          ) : error ? (
            <div className="bg-white rounded-2xl p-8 text-center text-red-500">{error}</div>
          ) : (
            <>
              {sub === "home" && (
                <HomePanel
                  data={data}
                  userId={userId}
                  onReload={load}
                  onAssist={(opts) => {
                    const next = new URLSearchParams(searchParams);
                    next.set("sub", "assist");
                    if (opts?.chapterId) next.set("chapter_id", String(opts.chapterId));
                    if (opts?.passage) next.set("passage", opts.passage.slice(0, 500));
                    if (opts?.mode) next.set("mode", opts.mode);
                    setSearchParams(next);
                  }}
                  onDiscuss={() => setSub("discuss")}
                  onMine={() => setSub("mine")}
                  onPlan={() => setSub("plan")}
                  onSummary={() => setSub("summary")}
                  onNeedLogin={() =>
                    navigate(
                      `/login?redirect=${encodeURIComponent("/book-club/ai-center")}`
                    )
                  }
                />
              )}
              {sub === "assist" && (
                <AssistPanel
                  data={data}
                  userId={userId}
                  initialMode={searchParams.get("mode") || ""}
                  initialChapterId={searchParams.get("chapter_id") || ""}
                  initialPassage={searchParams.get("passage") || ""}
                  onNeedLogin={() =>
                    navigate(`/login?redirect=${encodeURIComponent("/book-club/ai-center?sub=assist")}`)
                  }
                />
              )}
              {sub === "plan" && (
                <PlanPanel
                  data={data}
                  userId={userId}
                  onNeedLogin={() =>
                    navigate(`/login?redirect=${encodeURIComponent("/book-club/ai-center?sub=plan")}`)
                  }
                />
              )}
              {sub === "discuss" && (
                <DiscussPanel
                  data={data}
                  userId={userId}
                  onNeedLogin={() =>
                    navigate(`/login?redirect=${encodeURIComponent("/book-club/ai-center?sub=discuss")}`)
                  }
                />
              )}
              {sub === "summary" && (
                <SummaryPanel
                  data={data}
                  userId={userId}
                  onNeedLogin={() =>
                    navigate(`/login?redirect=${encodeURIComponent("/book-club/ai-center?sub=summary")}`)
                  }
                />
              )}
              {sub === "chapters" && (
                <ChaptersPanel data={data} userId={userId} onReload={load} onNeedLogin={() => navigate("/login")} />
              )}
              {sub === "mine" && (
                <MinePanel data={data} userId={userId} onNeedLogin={() => navigate("/login")} onReload={load} />
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function HomePanel({
  data,
  userId,
  onReload,
  onAssist,
  onDiscuss,
  onMine,
  onPlan,
  onSummary,
  onNeedLogin,
}) {
  const reading = data?.reading;
  const material = data?.material;
  const chapter = data?.current_chapter;
  const progress = data?.my_progress || { done: 0, total: 0 };
  const weekPlan = data?.week_plan;
  const todayTask = weekPlan?.today_task;
  const [reflection, setReflection] = useState("");
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkMsg, setCheckMsg] = useState("");

  const submitCheckin = async () => {
    if (!userId) {
      onNeedLogin?.();
      return;
    }
    if (!todayTask?.id || todayTask.is_rest) return;
    setCheckBusy(true);
    setCheckMsg("");
    try {
      await coReadPost(`/daily-tasks/${todayTask.id}/checkin`, {
        user_id: userId,
        reflection: reflection.trim(),
      });
      setCheckMsg("今日打卡成功");
      setReflection("");
      onReload?.();
    } catch (e) {
      setCheckMsg(e.message || "打卡失败");
    } finally {
      setCheckBusy(false);
    }
  };

  if (!reading) {
    return (
      <div className="bg-white rounded-2xl p-10 text-center text-gray-500">
        {data?.message || "暂无当期共读书目"}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-2xl border border-pink-100 p-6 flex flex-col md:flex-row gap-5">
        {reading.cover_url ? (
          <img
            src={assetUrl(reading.cover_url)}
            alt=""
            className="w-28 h-40 object-cover rounded-lg shadow shrink-0 mx-auto md:mx-0"
          />
        ) : (
          <div className="w-28 h-40 bg-amber-50 rounded-lg flex items-center justify-center text-gray-300 shrink-0 mx-auto md:mx-0">
            书
          </div>
        )}
        <div className="flex-1 space-y-2 text-center md:text-left">
          <p className="text-xs text-pink-500">{reading.period_label}</p>
          <h2 className="text-xl font-bold text-gray-800">{reading.book_title}</h2>
          {reading.book_author ? <p className="text-sm text-gray-500">作者：{reading.book_author}</p> : null}
          <p className="text-sm text-gray-600 line-clamp-3">{reading.summary}</p>
          <p className="text-xs text-gray-400">
            电子档：
            {material?.has_file
              ? `${material.file_name} · ${material.extract_status === "ok" || material.extract_status === "partial" ? `已提取约 ${material.char_count} 字` : material.extract_note || "待提取"}`
              : "尚未上传（请管理员在后台绑定）"}
          </p>
        </div>
      </section>

      {/* P2：本周共读计划（来自值日表同步） */}
      <section className="bg-white rounded-2xl border border-amber-100 p-5 space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-semibold text-gray-800">本周共读计划</h3>
          {weekPlan ? (
            <span className="text-xs text-amber-700">
              {weekPlan.start_date} — {weekPlan.end_date}
              {weekPlan.sync_status ? ` · ${weekPlan.sync_status}` : ""}
            </span>
          ) : null}
        </div>
        {!weekPlan ? (
          <p className="text-sm text-gray-500">
            暂无已同步的周计划。管理员请在「读书会管理 → 值日表」填完整后点「同步到共读中心」。
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              <strong>{weekPlan.book_title || reading.book_title}</strong>
              {weekPlan.week_label ? ` · ${weekPlan.week_label}` : ""}
            </p>
            {weekPlan.progress ? (
              <p className="text-xs text-teal-700">
                我的本周打卡：{weekPlan.progress.checked_days}/
                {weekPlan.progress.workday_total} 天
              </p>
            ) : null}
            <ul className="divide-y divide-gray-50">
              {(weekPlan.tasks || []).map((t) => (
                <li
                  key={t.id}
                  className={`py-2.5 text-sm ${
                    t.task_date === weekPlan.today_task?.task_date
                      ? "bg-amber-50/60 -mx-2 px-2 rounded-lg"
                      : ""
                  }`}
                >
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <span className="text-gray-500 w-24 shrink-0">
                      {t.task_date} {t.weekday_label}
                    </span>
                    {t.is_rest ? (
                      <span className="text-gray-400">休息</span>
                    ) : (
                      <>
                        <span className="text-gray-800 font-medium">
                          {t.reading_content || "（未填章节）"}
                        </span>
                        {t.checked ? (
                          <span className="text-teal-600 text-xs">已打卡</span>
                        ) : null}
                      </>
                    )}
                  </div>
                  {!t.is_rest ? (
                    <p className="text-xs text-gray-400 mt-0.5 pl-0 sm:pl-24">
                      主持 {t.host || "—"} · 唱歌 {t.singer || "—"} · 放映{" "}
                      {t.projector || "—"}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* P3：今日打卡 */}
      <section className="bg-white rounded-2xl border p-5 space-y-3">
        <h3 className="font-semibold text-gray-800">今日阅读打卡</h3>
        {!todayTask ? (
          <p className="text-sm text-gray-500">今天没有已同步的阅读任务。</p>
        ) : todayTask.is_rest ? (
          <p className="text-sm text-gray-500">今日休息，无需打卡。</p>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              今日阅读：<strong>{todayTask.reading_content}</strong>
            </p>
            <p className="text-xs text-gray-400">
              主持 {todayTask.host || "—"} · 唱歌 {todayTask.singer || "—"} · 放映{" "}
              {todayTask.projector || "—"}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  onAssist?.({
                    mode: todayTask.chapter_id ? "chapter" : "passage",
                    chapterId: todayTask.chapter_id || data?.current_chapter?.id || "",
                    passage: todayTask.reading_content || "",
                  })
                }
                className="px-4 py-2 rounded-full bg-rose-600 text-white text-sm"
              >
                这段内容我没读懂，问问 AI
              </button>
            </div>
            {todayTask.checked ? (
              <div className="space-y-2">
                <p className="text-sm text-teal-700 bg-teal-50 rounded-lg px-3 py-2">
                  今日已打卡
                  {todayTask.my_checkin?.reflection
                    ? ` · 感悟：${todayTask.my_checkin.reflection}`
                    : ""}
                </p>
                {todayTask.my_checkin?.id && todayTask.my_checkin?.reflection ? (
                  <button
                    type="button"
                    className="text-sm text-pink-600 underline"
                    onClick={async () => {
                      if (!userId) {
                        onNeedLogin?.();
                        return;
                      }
                      try {
                        await coReadPost("/share-checkin", {
                          user_id: userId,
                          checkin_id: todayTask.my_checkin.id,
                        });
                        setCheckMsg("已分享到共读讨论");
                        onDiscuss?.();
                      } catch (e) {
                        setCheckMsg(e.message || "分享失败");
                      }
                    }}
                  >
                    把感悟分享到讨论区
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value.slice(0, 2000))}
                  rows={3}
                  placeholder="阅读感悟（选填；填了可稍后分享到讨论）"
                  className="w-full border rounded-xl px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  disabled={checkBusy}
                  onClick={submitCheckin}
                  className="w-full py-2.5 rounded-full bg-amber-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {checkBusy ? "提交中…" : "完成今日打卡"}
                </button>
                <p className="text-[11px] text-gray-400">
                  北京时间 23:00 截止；不参加晨读也可打卡。AI 与感悟均非必填。
                </p>
              </>
            )}
            {checkMsg ? <p className="text-sm text-amber-800">{checkMsg}</p> : null}
          </>
        )}
      </section>

      {/* P6 本周成果三类人数 */}
      {data.week_outcome ? (
        <section className="bg-white rounded-2xl border border-teal-100 p-5 space-y-3">
          <h3 className="font-semibold text-gray-800">本周阅读成果（分类统计）</h3>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-xl bg-amber-50 p-3">
              <p className="text-lg font-bold text-amber-800">
                {data.week_outcome.planned_workdays}
              </p>
              <p className="text-xs text-gray-500">计划工作日</p>
            </div>
            <div className="rounded-xl bg-teal-50 p-3">
              <p className="text-lg font-bold text-teal-800">
                {data.week_outcome.async_checkin_people}
              </p>
              <p className="text-xs text-gray-500">自主打卡人数</p>
            </div>
            <div className="rounded-xl bg-pink-50 p-3">
              <p className="text-lg font-bold text-pink-800">
                {data.week_outcome.morning_checkin_people}
              </p>
              <p className="text-xs text-gray-500">晨读卡人数</p>
            </div>
          </div>
          <p className="text-[11px] text-gray-400">{data.week_outcome.note}</p>
          <button
            type="button"
            onClick={onSummary}
            className="text-sm text-amber-800 underline"
          >
            查看更多成果汇总
          </button>
        </section>
      ) : null}

      {/* P5 阅读动态 */}
      <section className="bg-white rounded-2xl border p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">共读交流 · 阅读感悟</h3>
          <button type="button" onClick={onDiscuss} className="text-sm text-pink-600 underline">
            进入讨论区
          </button>
        </div>
        {(data.reading_feed || []).length === 0 ? (
          <p className="text-sm text-gray-400">
            还没有公开感悟。打卡时可选填，并分享到讨论。
          </p>
        ) : (
          <ul className="space-y-3">
            {data.reading_feed.map((f) => (
              <li key={f.id} className="text-sm border-b border-gray-50 pb-2">
                <p className="text-gray-800">
                  <strong>{f.author_name}</strong>
                  <span className="text-xs text-gray-400 ml-2">{f.task_date}</span>
                </p>
                {f.reading_content ? (
                  <p className="text-xs text-amber-700 mt-0.5">读：{f.reading_content}</p>
                ) : null}
                <p className="text-gray-600 mt-1 whitespace-pre-wrap">{f.reflection}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border p-5 space-y-2">
          <h3 className="font-semibold text-gray-800">当前共读章节</h3>
          <p className="text-sm text-gray-600">{chapter?.title || "未设定，请先拆分或添加章节"}</p>
          <p className="text-xs text-gray-400">
            我的进度：{progress.done}/{progress.total} 章
          </p>
          {data.my_checkins ? (
            <p className="text-xs text-teal-700">
              累计打卡 {data.my_checkins.total_days} 天 · 连续{" "}
              {data.my_checkins.streak_days} 天 · 本周 {data.my_checkins.week_days} 天
            </p>
          ) : null}
          <button type="button" onClick={onMine} className="text-sm text-rose-600 underline">
            查看我的成长记录
          </button>
        </div>
        <div className="bg-white rounded-2xl border p-5 space-y-3">
          <h3 className="font-semibold text-gray-800">AI · 社交 · 更多</h3>
          <button
            type="button"
            onClick={() => onAssist?.({})}
            className="w-full py-2.5 rounded-full bg-rose-600 text-white text-sm font-medium"
          >
            AI 阅读助手
          </button>
          <button
            type="button"
            onClick={onDiscuss}
            className="w-full py-2.5 rounded-full border border-pink-200 text-pink-700 text-sm font-medium"
          >
            共读讨论与交流
          </button>
          <button
            type="button"
            onClick={onPlan}
            className="w-full py-2.5 rounded-full border border-rose-200 text-rose-700 text-sm font-medium"
          >
            我的个人 AI 计划
          </button>
          <button
            type="button"
            onClick={onSummary}
            className="w-full py-2.5 rounded-full border border-amber-200 text-amber-800 text-sm font-medium"
          >
            日 / 周 / 月成果汇总
          </button>
          <Link
            to="/reading/checkin"
            className="block text-center text-sm text-pink-600 hover:underline"
          >
            去写今日晨读收获（线上晨读卡）
          </Link>
        </div>
      </section>

      {(data.discussion_prompts || []).length > 0 && (
        <section className="bg-white rounded-2xl border p-5 space-y-2">
          <h3 className="font-semibold text-gray-800">今日讨论问题</h3>
          {data.discussion_prompts.map((t) => (
            <div key={t.id} className="text-sm border-b border-gray-50 pb-2">
              <p className="font-medium text-gray-800">
                {t.is_pinned ? "📌 " : ""}
                {t.title || t.content}
              </p>
              {t.title ? <p className="text-gray-500 mt-0.5 line-clamp-2">{t.content}</p> : null}
            </div>
          ))}
        </section>
      )}

      {(data.published_summaries || []).length > 0 && (
        <section className="bg-white rounded-2xl border border-amber-100 p-5 space-y-2">
          <h3 className="font-semibold text-gray-800">团体成果资料包</h3>
          {data.published_summaries.map((s) => (
            <details key={s.id} className="text-sm">
              <summary className="cursor-pointer text-amber-800">
                {s.title || `${s.period_type_label} · ${s.period_key}`}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-gray-700">{s.output_text}</pre>
            </details>
          ))}
        </section>
      )}

      <p className="text-sm text-gray-500 text-center">{data.message}</p>
    </div>
  );
}

function AssistPanel({
  data,
  userId,
  onNeedLogin,
  initialMode = "",
  initialChapterId = "",
  initialPassage = "",
}) {
  const reading = data?.reading;
  const chapters = data?.chapters || [];
  const modes = data?.modes || [];
  const [mode, setMode] = useState(initialMode || "chapter");
  const [chapterId, setChapterId] = useState(
    initialChapterId || data?.current_chapter?.id || ""
  );
  const [passage, setPassage] = useState(initialPassage || "");
  const [result, setResult] = useState("");
  const [outputId, setOutputId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (initialMode) setMode(initialMode);
    if (initialChapterId) setChapterId(initialChapterId);
    if (initialPassage) setPassage(initialPassage);
  }, [initialMode, initialChapterId, initialPassage]);

  useEffect(() => {
    if (!initialChapterId && data?.current_chapter?.id) {
      setChapterId(data.current_chapter.id);
    }
  }, [data?.current_chapter?.id, initialChapterId]);

  const run = async (force = false) => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    if (!reading) return;
    setBusy(true);
    setMsg("");
    setEditing(false);
    try {
      // 从今日任务带入时：passage 常是章节标题，请用户补充原文以获得更准回答
      const res = await coReadPost("/distill", {
        user_id: userId,
        mode,
        reading_id: reading.id,
        chapter_id: chapterId ? Number(chapterId) : null,
        passage,
        force,
      });
      setResult(res.item?.output_text || "");
      setOutputId(res.item?.id || null);
      setMsg(
        (res.cached ? "（缓存结果） " : "") +
          (res.message || "已生成") +
          (passage && mode === "passage"
            ? " · 建议粘贴书中原文后再问一次，理解会更准"
            : "")
      );
    } catch (e) {
      setMsg(e.message || "生成失败");
      setResult("");
      setOutputId(null);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!userId || !outputId) return;
    setBusy(true);
    try {
      const res = await coReadPost(`/outputs/${outputId}`, {
        user_id: userId,
        output_text: result,
      });
      setResult(res.item?.output_text || result);
      setEditing(false);
      setMsg(res.message || "已保存修改");
    } catch (e) {
      setMsg(e.message || "保存失败");
    } finally {
      setBusy(false);
    }
  };

  if (!reading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-400">暂无书目</div>;
  }

  return (
    <div className="bg-white rounded-2xl border border-pink-100 p-6 space-y-4">
      <h2 className="font-bold text-gray-800 text-lg">AI 阅读助手</h2>
      <p className="text-sm text-gray-500">
        基于本期已上传电子档 / 章节正文生成。无材料时不会编造全书内容。
      </p>

      <div className="flex flex-wrap gap-2">
        {modes.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => setMode(m.value)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              mode === m.value ? "bg-pink-500 text-white border-pink-500" : "bg-white"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {(mode === "chapter" || mode === "discuss" || mode === "book") && (
        <label className="block text-sm">
          <span className="text-gray-600">相关章节（单章模式必选有正文的章）</span>
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          >
            <option value="">不指定</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
                {c.has_body ? "" : "（无正文）"}
              </option>
            ))}
          </select>
        </label>
      )}

      {mode === "passage" && (
        <label className="block text-sm">
          <span className="text-gray-600">粘贴指定原文 *</span>
          <textarea
            value={passage}
            onChange={(e) => setPassage(e.target.value)}
            rows={5}
            className="mt-1 w-full border rounded-xl px-3 py-2"
            placeholder="粘贴本节或一段原文…"
          />
        </label>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => run(false)}
          className="px-5 py-2.5 rounded-full bg-rose-600 text-white text-sm disabled:opacity-50"
        >
          {busy ? "生成中…" : "生成"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(true)}
          className="px-5 py-2.5 rounded-full border text-sm disabled:opacity-50"
        >
          强制重新生成
        </button>
      </div>

      {msg && <p className="text-sm text-amber-700">{msg}</p>}
      {result && (
        <div className="space-y-2">
          {editing ? (
            <textarea
              value={result}
              onChange={(e) => setResult(e.target.value)}
              rows={12}
              className="w-full rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-sm leading-relaxed"
            />
          ) : (
            <div className="rounded-xl bg-amber-50/80 border border-amber-100 p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
              {result}
            </div>
          )}
          {outputId ? (
            <div className="flex gap-2">
              {editing ? (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={saveEdit}
                    className="px-4 py-1.5 rounded-full bg-rose-600 text-white text-sm"
                  >
                    保存修改
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-4 py-1.5 rounded-full border text-sm"
                  >
                    取消
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="px-4 py-1.5 rounded-full border text-sm text-rose-700"
                >
                  编辑并保存版本
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ChaptersPanel({ data, userId, onReload, onNeedLogin }) {
  const chapters = data?.chapters || [];
  const reading = data?.reading;
  const doneIds = new Set(data?.my_progress?.done_ids || []);
  const [activeId, setActiveId] = useState(null);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [loadingBody, setLoadingBody] = useState(false);

  const openChapter = async (c) => {
    setActiveId(c.id);
    setTitle(c.title);
    setBody("");
    setLoadingBody(true);
    try {
      const q = userId ? `?user_id=${userId}` : "";
      const d = await coReadGet(`/chapters/${c.id}${q}`);
      setBody(d.item?.body_text || "（本章暂无正文，请联系管理员补充）");
      setTitle(d.item?.title || c.title);
    } catch (e) {
      setBody(e.message || "加载失败");
    } finally {
      setLoadingBody(false);
    }
  };

  const markDone = async (chapterId) => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    try {
      await coReadPost("/progress", {
        user_id: userId,
        chapter_id: chapterId,
        status: "done",
      });
      onReload();
    } catch (e) {
      alert(e.message || "更新失败");
    }
  };

  if (!reading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-400">暂无书目</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border p-6 space-y-4">
        <h2 className="font-bold text-gray-800">《{reading.book_title}》章节</h2>
        {chapters.length === 0 ? (
          <p className="text-sm text-gray-500">
            暂无章节。请管理员上传电子档后「自动拆分」，或手动添加章节正文。
          </p>
        ) : (
          <ul className="space-y-3">
            {chapters.map((c) => (
              <li
                key={c.id}
                className={`border rounded-xl p-4 flex flex-wrap items-start justify-between gap-3 ${
                  activeId === c.id ? "border-rose-300 bg-rose-50/40" : "border-gray-100"
                }`}
              >
                <button type="button" className="text-left flex-1" onClick={() => openChapter(c)}>
                  <p className="font-medium text-gray-800">
                    {doneIds.has(c.id) ? "✓ " : ""}
                    {c.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {c.has_body ? `约 ${c.char_count} 字 · 点击阅读` : "尚无正文"}
                    {c.summary ? ` · ${c.summary}` : ""}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => markDone(c.id)}
                  className="text-sm px-3 py-1.5 rounded-full border text-pink-600"
                >
                  {doneIds.has(c.id) ? "已读" : "标记已读"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {activeId ? (
        <div className="bg-white rounded-2xl border border-rose-100 p-6 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-bold text-gray-800">{title}</h3>
            <button
              type="button"
              onClick={() => setActiveId(null)}
              className="text-sm text-gray-400"
            >
              收起
            </button>
          </div>
          {loadingBody ? (
            <p className="text-sm text-gray-400">加载正文…</p>
          ) : (
            <article className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed max-h-[70vh] overflow-y-auto">
              {body}
            </article>
          )}
          <button
            type="button"
            onClick={() => markDone(activeId)}
            className="px-4 py-2 rounded-full bg-rose-600 text-white text-sm"
          >
            读完，标记已读
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MinePanel({ data, userId, onNeedLogin, onReload }) {
  const reading = data?.reading;
  const [content, setContent] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [notes, setNotes] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const reloadMine = () => {
    if (!userId || !reading) return;
    coReadGet(`/notes?user_id=${userId}&reading_id=${reading.id}`)
      .then((d) => setNotes(d.items || []))
      .catch(() => setNotes([]));
    coReadGet(`/my-outputs?user_id=${userId}&reading_id=${reading.id}`)
      .then((d) => setOutputs(d.items || []))
      .catch(() => setOutputs([]));
  };

  useEffect(() => {
    reloadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reading]);

  const saveNote = async () => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    if (!reading) return;
    try {
      await coReadPost("/notes", {
        user_id: userId,
        reading_id: reading.id,
        chapter_id: data?.current_chapter?.id || null,
        content,
        note_type: noteType,
      });
      setContent("");
      onReload();
      reloadMine();
    } catch (e) {
      alert(e.message || "保存失败");
    }
  };

  const saveOutputEdit = async (id) => {
    try {
      await coReadPost(`/outputs/${id}`, { user_id: userId, output_text: editText });
      setEditId(null);
      reloadMine();
    } catch (e) {
      alert(e.message || "保存失败");
    }
  };

  if (!userId) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <p className="text-gray-500 mb-3">登录后可保存私人笔记与查看历史提炼</p>
        <button type="button" onClick={onNeedLogin} className="px-5 py-2 rounded-full bg-pink-500 text-white text-sm">
          去登录
        </button>
      </div>
    );
  }

  const checkins = data?.my_checkins;

  return (
    <div className="space-y-4">
      {checkins ? (
        <div className="bg-white rounded-2xl border border-teal-100 p-6 space-y-3">
          <h2 className="font-bold text-gray-800">我的阅读打卡</h2>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-xl bg-teal-50 p-3">
              <p className="text-lg font-bold text-teal-800">{checkins.total_days}</p>
              <p className="text-xs text-gray-500">累计天数</p>
            </div>
            <div className="rounded-xl bg-amber-50 p-3">
              <p className="text-lg font-bold text-amber-800">{checkins.week_days}</p>
              <p className="text-xs text-gray-500">本周</p>
            </div>
            <div className="rounded-xl bg-rose-50 p-3">
              <p className="text-lg font-bold text-rose-800">{checkins.streak_days}</p>
              <p className="text-xs text-gray-500">连续</p>
            </div>
          </div>
          {(checkins.recent || []).length > 0 ? (
            <ul className="space-y-2 text-sm max-h-64 overflow-y-auto">
              {checkins.recent.map((r) => (
                <li key={r.id} className="border-b border-gray-50 pb-2">
                  <p className="text-gray-500 text-xs">
                    {r.task_date}
                    {r.reading_content ? ` · ${r.reading_content}` : ""}
                  </p>
                  {r.reflection ? (
                    <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{r.reflection}</p>
                  ) : (
                    <p className="text-gray-400 text-xs mt-0.5">（无感悟）</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">还没有打卡记录</p>
          )}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border p-6 space-y-3">
        <h2 className="font-bold text-gray-800">写一条笔记 / 金句</h2>
        <div className="flex gap-2 text-sm">
          <button
            type="button"
            onClick={() => setNoteType("note")}
            className={`px-3 py-1 rounded-full border ${noteType === "note" ? "bg-rose-500 text-white border-rose-500" : ""}`}
          >
            笔记
          </button>
          <button
            type="button"
            onClick={() => setNoteType("quote")}
            className={`px-3 py-1 rounded-full border ${noteType === "quote" ? "bg-rose-500 text-white border-rose-500" : ""}`}
          >
            金句
          </button>
        </div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          className="w-full border rounded-xl px-3 py-2 text-sm"
          placeholder="仅自己可见"
        />
        <button
          type="button"
          onClick={saveNote}
          className="px-5 py-2 rounded-full bg-rose-600 text-white text-sm"
        >
          保存
        </button>
        <ul className="space-y-2 pt-2">
          {notes.map((n) => (
            <li key={n.id} className="text-sm text-gray-700 border-b border-gray-50 pb-2">
              <span className="text-xs text-pink-500 mr-2">{n.note_type === "quote" ? "金句" : "笔记"}</span>
              {n.content}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-2xl border p-6 space-y-3">
        <h2 className="font-bold text-gray-800">我的 AI 提炼记录</h2>
        {outputs.length === 0 ? (
          <p className="text-sm text-gray-400">暂无</p>
        ) : (
          outputs.map((o) => (
            <details key={o.id} className="border rounded-xl p-3 text-sm">
              <summary className="cursor-pointer font-medium">
                {o.mode_label} · {o.created_at?.replace("T", " ").slice(0, 16)}
              </summary>
              {editId === o.id ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={8}
                    className="w-full border rounded-xl px-3 py-2"
                  />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => saveOutputEdit(o.id)} className="px-3 py-1.5 rounded-full bg-rose-600 text-white text-xs">
                      保存
                    </button>
                    <button type="button" onClick={() => setEditId(null)} className="px-3 py-1.5 rounded-full border text-xs">
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <pre className="mt-2 whitespace-pre-wrap text-gray-700 font-sans">{o.output_text}</pre>
                  <button
                    type="button"
                    className="mt-2 text-xs text-rose-600"
                    onClick={() => {
                      setEditId(o.id);
                      setEditText(o.output_text || "");
                    }}
                  >
                    编辑
                  </button>
                </>
              )}
            </details>
          ))
        )}
      </div>
    </div>
  );
}

function PlanPanel({ data, userId, onNeedLogin }) {
  const reading = data?.reading;
  const [meta, setMeta] = useState(null);
  const [mood, setMood] = useState("calm");
  const [focus, setFocus] = useState("growth");
  const [minutes, setMinutes] = useState(20);
  const [style, setStyle] = useState("read_first");
  const [chapterId, setChapterId] = useState(data?.current_chapter?.id || "");
  const [result, setResult] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    coReadGet("/plan/meta").then(setMeta).catch(() => {});
  }, []);

  useEffect(() => {
    if (data?.current_chapter?.id) setChapterId(data.current_chapter.id);
  }, [data?.current_chapter?.id]);

  useEffect(() => {
    if (!userId || !reading) return;
    coReadGet(`/plan/mine?user_id=${userId}&reading_id=${reading.id}`)
      .then((d) => setHistory(d.items || []))
      .catch(() => setHistory([]));
  }, [userId, reading]);

  const generate = async () => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    if (!reading) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await coReadPost("/plan", {
        user_id: userId,
        reading_id: reading.id,
        chapter_id: chapterId ? Number(chapterId) : null,
        mood,
        focus,
        minutes: Number(minutes),
        style,
      });
      setResult(res.item?.output_text || "");
      setMsg(res.message || "已生成");
      const d = await coReadGet(`/plan/mine?user_id=${userId}&reading_id=${reading.id}`);
      setHistory(d.items || []);
    } catch (e) {
      setMsg(e.message || "生成失败");
    } finally {
      setBusy(false);
    }
  };

  if (!reading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-400">暂无书目</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-pink-100 p-6 space-y-4">
        <h2 className="font-bold text-gray-800 text-lg">今日个性化阅读计划</h2>
        <p className="text-sm text-gray-500">
          基于当期共读书与你的自愿选择生成；不改变集体进度；内容仅你自己可见。
        </p>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <label>
            <span className="text-gray-600">今日状态</span>
            <select value={mood} onChange={(e) => setMood(e.target.value)} className="mt-1 w-full border rounded-xl px-3 py-2">
              {(meta?.moods || [{ value: "calm", label: "平静" }]).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-gray-600">近期关注</span>
            <select value={focus} onChange={(e) => setFocus(e.target.value)} className="mt-1 w-full border rounded-xl px-3 py-2">
              {(meta?.focuses || [{ value: "growth", label: "个人成长" }]).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-gray-600">可用时间（分钟）</span>
            <select value={minutes} onChange={(e) => setMinutes(e.target.value)} className="mt-1 w-full border rounded-xl px-3 py-2">
              {(meta?.minute_options || [10, 20, 40]).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-gray-600">阅读方式</span>
            <select value={style} onChange={(e) => setStyle(e.target.value)} className="mt-1 w-full border rounded-xl px-3 py-2">
              {(meta?.styles || [{ value: "read_first", label: "先读原文" }]).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-gray-600">围绕章节</span>
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          >
            <option value="">当前共读 / 自动</option>
            {(data?.chapters || []).map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          disabled={busy}
          onClick={generate}
          className="px-5 py-2.5 rounded-full bg-rose-600 text-white text-sm disabled:opacity-50"
        >
          {busy ? "生成中…" : "生成今日计划"}
        </button>
        {msg && <p className="text-sm text-amber-700">{msg}</p>}
        {result && (
          <div className="rounded-xl bg-amber-50/80 border border-amber-100 p-4 text-sm whitespace-pre-wrap leading-relaxed">
            {result}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="bg-white rounded-2xl border p-5 space-y-2">
          <h3 className="font-semibold text-gray-800">最近计划</h3>
          {history.slice(0, 5).map((p) => (
            <details key={p.id} className="text-sm border rounded-xl p-3">
              <summary className="cursor-pointer">
                {p.plan_date} · {p.mood_label} · {p.minutes} 分钟
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-gray-700">{p.output_text}</pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function DiscussPanel({ data, userId, onNeedLogin }) {
  const reading = data?.reading;
  const [chapterId, setChapterId] = useState(data?.current_chapter?.id || "");
  const [threads, setThreads] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [msg, setMsg] = useState("");
  const [revealed, setRevealed] = useState({});

  const loadThreads = () => {
    if (!reading) return;
    const q = new URLSearchParams({ reading_id: String(reading.id) });
    if (chapterId) q.set("chapter_id", String(chapterId));
    if (userId) q.set("user_id", String(userId));
    coReadGet(`/threads?${q}`)
      .then((d) => setThreads(d.items || []))
      .catch(() => setThreads([]));
  };

  useEffect(() => {
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reading?.id, chapterId, userId]);

  const post = async () => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    try {
      await coReadPost("/threads", {
        user_id: userId,
        reading_id: reading.id,
        chapter_id: chapterId ? Number(chapterId) : null,
        title,
        content,
      });
      setContent("");
      setTitle("");
      setMsg("已发布");
      loadThreads();
    } catch (e) {
      setMsg(e.message || "发布失败");
    }
  };

  const toggleLike = async (id) => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    try {
      const res = await coReadPost(`/threads/${id}/like`, { user_id: userId });
      setThreads((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, like_count: res.like_count, liked_by_me: res.liked }
            : t
        )
      );
    } catch (e) {
      alert(e.message || "操作失败");
    }
  };

  const openThread = async (id) => {
    setActiveId(id);
    try {
      const d = await coReadGet(`/threads/${id}/replies`);
      setReplies(d.items || []);
    } catch {
      setReplies([]);
    }
  };

  const sendReply = async () => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    if (!activeId) return;
    try {
      await coReadPost(`/threads/${activeId}/replies`, {
        user_id: userId,
        content: replyText,
      });
      setReplyText("");
      openThread(activeId);
      loadThreads();
    } catch (e) {
      alert(e.message || "回复失败");
    }
  };

  if (!reading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-400">暂无书目</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border p-6 space-y-3">
        <h2 className="font-bold text-gray-800">共读讨论</h2>
        <p className="text-sm text-gray-500">
          主动发帖才会公开；私人笔记不会自动出现。超前章节讨论会折叠防剧透。
        </p>
        <label className="block text-sm">
          <span className="text-gray-600">关联章节（可选）</span>
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          >
            <option value="">全部章节</option>
            {(data?.chapters || []).map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="标题（可选）"
          className="w-full border rounded-xl px-3 py-2 text-sm"
        />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          placeholder="分享心得、金句或疑问…"
          className="w-full border rounded-xl px-3 py-2 text-sm"
        />
        <button type="button" onClick={post} className="px-5 py-2 rounded-full bg-rose-600 text-white text-sm">
          发布讨论
        </button>
        {msg && <p className="text-sm text-amber-700">{msg}</p>}
      </div>

      <div className="bg-white rounded-2xl border p-5 space-y-3">
        {threads.length === 0 ? (
          <p className="text-sm text-gray-400">还没有讨论，来发第一条吧</p>
        ) : (
          threads.map((t) => {
            const showFull = revealed[t.id] || !t.is_spoiler;
            const display = showFull ? t.full_content || t.content : t.content;
            return (
              <div key={t.id} className="border-b border-gray-50 pb-3">
                <div className="flex gap-2 items-start">
                  <button type="button" className="text-left flex-1" onClick={() => openThread(t.id)}>
                    <p className="font-medium text-gray-800">
                      {t.is_pinned ? "📌 " : ""}
                      {t.title || (showFull ? display : "可能剧透的讨论").slice(0, 40)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t.author_name} · {t.reply_count || 0} 回复 · {t.like_count || 0} 赞
                    </p>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-3">{display}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleLike(t.id)}
                    className={`text-xs px-2 py-1 rounded-full border shrink-0 ${
                      t.liked_by_me ? "bg-rose-100 text-rose-700 border-rose-200" : "text-gray-500"
                    }`}
                  >
                    {t.liked_by_me ? "已赞" : "赞"}
                  </button>
                </div>
                {t.is_spoiler && !revealed[t.id] ? (
                  <button
                    type="button"
                    className="mt-2 text-xs text-amber-700 underline"
                    onClick={() => setRevealed((prev) => ({ ...prev, [t.id]: true }))}
                  >
                    我已读过相关章节，展开全文
                  </button>
                ) : null}
                {activeId === t.id && (
                  <div className="mt-3 ml-2 space-y-2">
                    {replies.map((r) => (
                      <div key={r.id} className="text-sm bg-rose-50/50 rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-400">{r.author_name}：</span>
                        {r.content}
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        className="flex-1 border rounded-xl px-3 py-2 text-sm"
                        placeholder="写回复…"
                      />
                      <button type="button" onClick={sendReply} className="px-3 py-2 rounded-xl bg-gray-800 text-white text-sm">
                        回复
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function SummaryPanel({ data, userId, onNeedLogin }) {
  const reading = data?.reading;
  const [meta, setMeta] = useState(null);
  const [periodType, setPeriodType] = useState("day");
  const [result, setResult] = useState("");
  const [stats, setStats] = useState(null);
  const [mine, setMine] = useState([]);
  const [published, setPublished] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const reloadLists = () => {
    if (!reading) return;
    if (userId) {
      coReadGet(`/summaries/mine?user_id=${userId}&reading_id=${reading.id}`)
        .then((d) => setMine(d.items || []))
        .catch(() => setMine([]));
    }
    coReadGet(`/summaries/published?reading_id=${reading.id}`)
      .then((d) => setPublished(d.items || []))
      .catch(() => setPublished([]));
  };

  useEffect(() => {
    coReadGet("/summaries/meta").then(setMeta).catch(() => {});
  }, []);

  useEffect(() => {
    reloadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reading?.id]);

  const generate = async () => {
    if (!userId) {
      onNeedLogin();
      return;
    }
    if (!reading) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await coReadPost("/summaries", {
        user_id: userId,
        reading_id: reading.id,
        period_type: periodType,
      });
      setResult(res.item?.output_text || "");
      setStats(res.item?.stats || null);
      setMsg(
        res.has_data === false
          ? `${res.message || "已生成"}（本周期记录较少，内容偏简略）`
          : res.message || "已生成"
      );
      reloadLists();
    } catch (e) {
      setMsg(e.message || "生成失败");
    } finally {
      setBusy(false);
    }
  };

  if (!reading) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-400">暂无书目</div>;
  }

  const outcome = data?.week_outcome;

  return (
    <div className="space-y-4">
      {outcome ? (
        <div className="bg-white rounded-2xl border border-teal-100 p-6 space-y-3">
          <h2 className="font-bold text-gray-800">本周集体成果（值日表同步）</h2>
          <p className="text-sm text-gray-600">
            {outcome.book_title} · {outcome.week_label} · {outcome.start_date}—{outcome.end_date}
          </p>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-xl bg-amber-50 p-3">
              <p className="text-lg font-bold text-amber-800">{outcome.planned_workdays}</p>
              <p className="text-xs text-gray-500">计划工作日</p>
            </div>
            <div className="rounded-xl bg-teal-50 p-3">
              <p className="text-lg font-bold text-teal-800">{outcome.async_checkin_people}</p>
              <p className="text-xs text-gray-500">自主打卡人数</p>
            </div>
            <div className="rounded-xl bg-pink-50 p-3">
              <p className="text-lg font-bold text-pink-800">{outcome.morning_checkin_people}</p>
              <p className="text-xs text-gray-500">晨读卡人数</p>
            </div>
          </div>
          <p className="text-[11px] text-gray-400">{outcome.note}</p>
          {(outcome.reflections || []).length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">本周感悟摘录</p>
              {outcome.reflections.slice(0, 8).map((r, i) => (
                <p key={i} className="text-sm text-gray-600 border-b border-gray-50 pb-1">
                  <span className="text-xs text-gray-400">{r.task_date} · </span>
                  {r.reflection}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-amber-100 p-6 space-y-4">
        <h2 className="font-bold text-gray-800 text-lg">日 / 周 / 月成果汇总</h2>
        <p className="text-sm text-gray-500">
          {meta?.hint ||
            "基于你的进度、笔记、AI 提炼与公开讨论整理；个人汇总仅自己可见。"}
        </p>

        <div className="flex flex-wrap gap-2">
          {(meta?.period_types || [
            { value: "day", label: "每日晨读汇总" },
            { value: "week", label: "每周阅读总结" },
            { value: "month", label: "每月线下资料包" },
          ]).map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setPeriodType(o.value)}
              className={`px-4 py-2 rounded-full text-sm border ${
                periodType === o.value
                  ? "bg-amber-600 text-white border-amber-600"
                  : "bg-white text-gray-600 border-gray-200"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={generate}
          className="px-5 py-2.5 rounded-full bg-amber-600 text-white text-sm disabled:opacity-50"
        >
          {busy ? "整理中…" : "生成我的个人汇总"}
        </button>
        {msg && <p className="text-sm text-amber-800">{msg}</p>}
        {stats && (
          <p className="text-xs text-gray-400">
            证据：进度 {stats.progress_updates || 0} · 笔记 {stats.notes || 0} · AI{" "}
            {stats.ai_outputs || 0} · 计划 {stats.plans || 0} · 讨论 {stats.threads || 0}
          </p>
        )}
        {result && (
          <div className="space-y-2">
            <div className="rounded-xl bg-amber-50/80 border border-amber-100 p-4 text-sm whitespace-pre-wrap leading-relaxed">
              {result}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-full border"
                onClick={() => {
                  navigator.clipboard?.writeText(result);
                  setMsg("已复制到剪贴板");
                }}
              >
                复制
              </button>
              <button
                type="button"
                className="text-xs px-3 py-1.5 rounded-full border"
                onClick={() => {
                  const blob = new Blob([result], { type: "text/plain;charset=utf-8" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `共读汇总-${periodType}.txt`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                导出 TXT
              </button>
            </div>
          </div>
        )}
      </div>

      {published.length > 0 && (
        <div className="bg-white rounded-2xl border p-5 space-y-2">
          <h3 className="font-semibold text-gray-800">团体已发布资料包</h3>
          <p className="text-xs text-gray-400">由管理员审改发布，可用于线下共读。</p>
          {published.map((s) => (
            <details key={s.id} className="text-sm border rounded-xl p-3">
              <summary className="cursor-pointer">
                {s.title || `${s.period_type_label} · ${s.period_key}`}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-gray-700">{s.output_text}</pre>
              <button
                type="button"
                className="mt-2 text-xs text-amber-700"
                onClick={() => {
                  const blob = new Blob([s.output_text || ""], {
                    type: "text/plain;charset=utf-8",
                  });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `${s.period_key || "pack"}.txt`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}
              >
                导出 TXT
              </button>
            </details>
          ))}
        </div>
      )}

      {mine.length > 0 && (
        <div className="bg-white rounded-2xl border p-5 space-y-2">
          <h3 className="font-semibold text-gray-800">我的最近汇总</h3>
          {mine.slice(0, 8).map((s) => (
            <details key={s.id} className="text-sm border rounded-xl p-3">
              <summary className="cursor-pointer">
                {s.period_type_label} · {s.period_key}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-gray-700">{s.output_text}</pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
